import type { SandGrid } from './sandgrid/SandGrid';

export interface SandcastlePreset {
  name: string;
  description: string;
  apply: (sandGrid: SandGrid, centerX: number, centerY: number, scale: number) => void;
}

export class SandcastlePresets {
  private static presets: Map<string, SandcastlePreset> = new Map();

  public static register(preset: SandcastlePreset): void {
    this.presets.set(preset.name, preset);
  }

  public static get(name: string): SandcastlePreset | undefined {
    return this.presets.get(name);
  }

  public static getAll(): SandcastlePreset[] {
    return Array.from(this.presets.values());
  }

  public static apply(
    name: string,
    sandGrid: SandGrid,
    centerX: number,
    centerY: number,
    scale: number = 1.0
  ): boolean {
    const preset = this.presets.get(name);
    if (!preset) return false;

    preset.apply(sandGrid, centerX, centerY, scale);
    return true;
  }

  // Helper function to set sand height with smooth falloff
  private static setSandWithFalloff(
    sandGrid: SandGrid,
    x: number,
    y: number,
    targetHeight: number,
    falloff: number = 1.0
  ): void {
    const currentHeight = sandGrid.get(x, y);
    const newHeight = Math.max(currentHeight, targetHeight * falloff);
    sandGrid.set(x, y, newHeight);
  }

  // Helper to draw a circle
  private static drawCircle(
    sandGrid: SandGrid,
    centerX: number,
    centerY: number,
    radius: number,
    height: number,
    hollow: boolean = false,
    wallThickness: number = 2
  ): void {
    const startRadius = hollow ? Math.max(0, radius - wallThickness) : 0;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= radius && dist >= startRadius) {
          const x = Math.round(centerX + dx);
          const y = Math.round(centerY + dy);

          // Calculate falloff from edge
          const edgeDist = hollow
            ? Math.min(radius - dist, dist - startRadius)
            : radius - dist;
          const falloff = Math.min(1.0, edgeDist / 2 + 0.5);

          this.setSandWithFalloff(sandGrid, x, y, height, falloff);
        }
      }
    }
  }

  // Helper to draw a cone
  private static drawCone(
    sandGrid: SandGrid,
    centerX: number,
    centerY: number,
    radius: number,
    maxHeight: number
  ): void {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= radius) {
          const x = Math.round(centerX + dx);
          const y = Math.round(centerY + dy);

          const heightRatio = 1.0 - dist / radius;
          const height = maxHeight * heightRatio;

          this.setSandWithFalloff(sandGrid, x, y, height, 1.0);
        }
      }
    }
  }

  // Helper to draw a rectangular tower
  private static drawTower(
    sandGrid: SandGrid,
    centerX: number,
    centerY: number,
    width: number,
    depth: number,
    height: number,
    hollow: boolean = false
  ): void {
    const halfW = width / 2;
    const halfD = depth / 2;

    for (let dy = -halfD; dy <= halfD; dy++) {
      for (let dx = -halfW; dx <= halfW; dx++) {
        if (hollow) {
          // Only draw walls
          const isWall =
            Math.abs(dx) >= halfW - 1 ||
            Math.abs(dy) >= halfD - 1;

          if (!isWall) continue;
        }

        const x = Math.round(centerX + dx);
        const y = Math.round(centerY + dy);

        // Edge falloff
        const distToEdge = Math.min(
          Math.min(halfW - Math.abs(dx), halfD - Math.abs(dy)),
          2
        );
        const falloff = Math.min(1.0, distToEdge / 2 + 0.5);

        this.setSandWithFalloff(sandGrid, x, y, height, falloff);
      }
    }
  }

  // Initialize default presets
  public static initializeDefaults(): void {
    // 1. Simple Castle - Single tower with walls
    this.register({
      name: 'Simple Castle',
      description: 'A basic castle with a central tower and surrounding walls',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;

        // Outer walls (square)
        this.drawTower(sandGrid, cx, cy, 30 * s, 30 * s, 8 * s, true);

        // Corner towers
        this.drawCircle(sandGrid, cx - 12 * s, cy - 12 * s, 5 * s, 12 * s);
        this.drawCircle(sandGrid, cx + 12 * s, cy - 12 * s, 5 * s, 12 * s);
        this.drawCircle(sandGrid, cx - 12 * s, cy + 12 * s, 5 * s, 12 * s);
        this.drawCircle(sandGrid, cx + 12 * s, cy + 12 * s, 5 * s, 12 * s);

        // Central tower
        this.drawCircle(sandGrid, cx, cy, 8 * s, 16 * s);
      },
    });

    // 2. Pyramid Castle - Stepped pyramid
    this.register({
      name: 'Pyramid Castle',
      description: 'A stepped pyramid with a spire on top',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;

        // Base
        this.drawTower(sandGrid, cx, cy, 32 * s, 32 * s, 6 * s);

        // Second level
        this.drawTower(sandGrid, cx, cy, 24 * s, 24 * s, 10 * s);

        // Third level
        this.drawTower(sandGrid, cx, cy, 16 * s, 16 * s, 14 * s);

        // Top spire
        this.drawCone(sandGrid, cx, cy, 8 * s, 20 * s);
      },
    });

    // 3. Twin Towers - Two tall towers connected by a wall
    this.register({
      name: 'Twin Towers',
      description: 'Two tall towers connected by a fortified wall',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;
        const spacing = 20 * s;

        // Left tower
        this.drawCircle(sandGrid, cx - spacing / 2, cy, 7 * s, 18 * s);

        // Right tower
        this.drawCircle(sandGrid, cx + spacing / 2, cy, 7 * s, 18 * s);

        // Connecting wall
        this.drawTower(
          sandGrid,
          cx,
          cy,
          spacing,
          4 * s,
          10 * s,
          true
        );

        // Wall battlements
        for (let i = -2; i <= 2; i++) {
          if (i === 0) continue;
          this.drawCircle(
            sandGrid,
            cx + i * 4 * s,
            cy,
            2 * s,
            12 * s
          );
        }
      },
    });

    // 4. Fortress Ring - Circular fortress with multiple towers
    this.register({
      name: 'Fortress Ring',
      description: 'A circular fortress with evenly spaced towers',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;
        const radius = 18 * s;
        const numTowers = 8;

        // Outer ring wall
        this.drawCircle(sandGrid, cx, cy, radius, 10 * s, true, 3 * s);

        // Place towers around the ring
        for (let i = 0; i < numTowers; i++) {
          const angle = (i / numTowers) * Math.PI * 2;
          const tx = cx + Math.cos(angle) * radius;
          const ty = cy + Math.sin(angle) * radius;

          this.drawCircle(sandGrid, tx, ty, 4 * s, 14 * s);
        }

        // Central keep
        this.drawCircle(sandGrid, cx, cy, 6 * s, 16 * s);
      },
    });

    // 5. Grand Palace - Large sprawling complex
    this.register({
      name: 'Grand Palace',
      description: 'An elaborate palace with multiple wings and courtyards',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;

        // Main central structure
        this.drawTower(sandGrid, cx, cy, 20 * s, 20 * s, 12 * s);

        // Four wings
        this.drawTower(sandGrid, cx - 18 * s, cy, 12 * s, 16 * s, 10 * s);
        this.drawTower(sandGrid, cx + 18 * s, cy, 12 * s, 16 * s, 10 * s);
        this.drawTower(sandGrid, cx, cy - 18 * s, 16 * s, 12 * s, 10 * s);
        this.drawTower(sandGrid, cx, cy + 18 * s, 16 * s, 12 * s, 10 * s);

        // Corner domes
        this.drawCone(sandGrid, cx - 16 * s, cy - 16 * s, 6 * s, 16 * s);
        this.drawCone(sandGrid, cx + 16 * s, cy - 16 * s, 6 * s, 16 * s);
        this.drawCone(sandGrid, cx - 16 * s, cy + 16 * s, 6 * s, 16 * s);
        this.drawCone(sandGrid, cx + 16 * s, cy + 16 * s, 6 * s, 16 * s);

        // Central dome
        this.drawCone(sandGrid, cx, cy, 10 * s, 18 * s);
      },
    });

    // 6. Dragon's Lair - Organic, cave-like structure
    this.register({
      name: "Dragon's Lair",
      description: 'A naturally formed mountain with cave entrances',
      apply: (sandGrid, cx, cy, scale) => {
        const s = scale;

        // Main mountain
        this.drawCone(sandGrid, cx, cy, 25 * s, 20 * s);

        // Additional peaks
        this.drawCone(sandGrid, cx - 15 * s, cy - 8 * s, 12 * s, 16 * s);
        this.drawCone(sandGrid, cx + 12 * s, cy - 10 * s, 10 * s, 14 * s);
        this.drawCone(sandGrid, cx - 8 * s, cy + 14 * s, 14 * s, 18 * s);

        // Smaller outcroppings
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2 + 0.3;
          const dist = 18 * s + Math.random() * 8 * s;
          const px = cx + Math.cos(angle) * dist;
          const py = cy + Math.sin(angle) * dist;
          const pRadius = 4 * s + Math.random() * 4 * s;

          this.drawCone(sandGrid, px, py, pRadius, 8 * s + Math.random() * 6 * s);
        }
      },
    });
  }
}

// Initialize defaults when module loads
SandcastlePresets.initializeDefaults();
